import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, unref as _unref, normalizeStyle as _normalizeStyle, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "bg-gradient-to-br from-white via-blue-50 to-indigo-50 rounded-xl sm:rounded-2xl shadow-lg sm:shadow-2xl p-4 sm:p-6 lg:p-8 border border-blue-100" }
const _hoisted_2 = { class: "flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 sm:mb-6 gap-3 sm:gap-0" }
const _hoisted_3 = { class: "flex items-center gap-3" }
const _hoisted_4 = { class: "bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-4 sm:px-5 py-2 sm:py-2.5 rounded-lg sm:rounded-xl text-xs sm:text-sm font-bold shadow-lg hover:shadow-xl transition-shadow" }
const _hoisted_5 = { class: "bg-white rounded-lg sm:rounded-xl shadow-md sm:shadow-lg p-3 sm:p-4 lg:p-6 border border-slate-100" }
const _hoisted_6 = { class: "mt-4 sm:mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4" }
const _hoisted_7 = { class: "bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-3 sm:p-4 border border-blue-200" }
const _hoisted_8 = { class: "flex items-center gap-2 sm:gap-3" }
const _hoisted_9 = { class: "min-w-0 flex-1" }
const _hoisted_10 = { class: "text-sm sm:text-base lg:text-lg font-bold text-blue-900 truncate" }
const _hoisted_11 = { class: "bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-lg p-3 sm:p-4 border border-emerald-200" }
const _hoisted_12 = { class: "flex items-center gap-2 sm:gap-3" }
const _hoisted_13 = { class: "min-w-0 flex-1" }
const _hoisted_14 = { class: "text-sm sm:text-base lg:text-lg font-bold text-emerald-900 truncate" }

import { computed } from 'vue';
import VChart from 'vue-echarts';
import { use } from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import { BarChart } from 'echarts/charts';
import { 
  GridComponent, 
  TooltipComponent, 
  LegendComponent
} from 'echarts/components';
import { ClienteTopComprasInterface } from '../interfaces/clienteTopCompras.interface';

interface Props {
  data?: ClienteTopComprasInterface;
  loading?: boolean;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'TopClientesChart',
  props: {
    data: {},
    loading: { type: Boolean }
  },
  setup(__props: any) {

use([
  CanvasRenderer, 
  BarChart, 
  GridComponent, 
  TooltipComponent, 
  LegendComponent
]);

const props = __props;

// Altura responsiva del gráfico
const chartHeight = computed(() => {
  const clientCount = props.data?.data.length || 5;
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;
  const baseHeight = isMobile ? 60 : 70;
  const minHeight = isMobile ? 350 : 450;
  return `${Math.max(minHeight, clientCount * baseHeight + 100)}px`;
});

const chartOption = computed(() => {
  const clientes = props.data?.data || [];
  
  const nombres = clientes.map(c => 
    `${c.cliente.nombreCliente} ${c.cliente.apellidoCliente}`
  );
  const totales = clientes.map(c => parseFloat(c.totalCompras));
  const cantidades = clientes.map(c => c.cantidadPedidos);

  // Configuración responsiva
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;
  const isTablet = typeof window !== 'undefined' && window.innerWidth < 1024;

  return {
    tooltip: {
      trigger: 'axis',
      axisPointer: { 
        type: 'shadow',
        shadowStyle: {
          color: 'rgba(59, 130, 246, 0.1)'
        }
      },
      backgroundColor: 'rgba(255, 255, 255, 0.95)',
      borderColor: '#3b82f6',
      borderWidth: 2,
      padding: isMobile ? 10 : 12,
      textStyle: {
        color: '#1e293b',
        fontSize: isMobile ? 11 : 13
      }
    },
    legend: {
      data: ['Total Compras', 'Cantidad Pedidos'],
      top: isMobile ? 8 : 10,
      textStyle: {
        fontSize: isMobile ? 10 : isTablet ? 11 : 13,
        fontWeight: 600
      },
      itemWidth: isMobile ? 14 : 18,
      itemHeight: isMobile ? 14 : 18,
      itemGap: isMobile ? 12 : 20
    },
    grid: {
      left: isMobile ? '8%' : '5%',
      right: isMobile ? '10%' : '8%',
      bottom: '5%',
      top: isMobile ? '18%' : '15%',
      containLabel: true
    },
    xAxis: {
      type: 'value',
      splitLine: {
        lineStyle: {
          type: 'dashed',
          color: '#e2e8f0'
        }
      },
      axisLine: {
        lineStyle: {
          color: '#cbd5e1'
        }
      },
      axisLabel: {
        color: '#64748b',
        fontSize: isMobile ? 9 : isTablet ? 10 : 12
      }
    },
    yAxis: {
      type: 'category',
      data: nombres,
      inverse: true,
      axisLine: {
        lineStyle: {
          color: '#cbd5e1'
        }
      },
      axisLabel: {
        fontSize: isMobile ? 9 : isTablet ? 10 : 12,
        width: isMobile ? 70 : isTablet ? 90 : 120,
        overflow: 'truncate',
        color: '#334155',
        fontWeight: 500
      },
      axisTick: {
        show: false
      }
    },
    series: [
      {
        name: 'Total Compras',
        type: 'bar',
        data: totales,
        barWidth: isMobile ? '35%' : '40%',
        itemStyle: {
          color: {
            type: 'linear',
            x: 0,
            y: 0,
            x2: 1,
            y2: 0,
            colorStops: [
              { offset: 0, color: '#3b82f6' },
              { offset: 1, color: '#1d4ed8' }
            ]
          },
          borderRadius: [0, isMobile ? 6 : 8, isMobile ? 6 : 8, 0],
          shadowColor: 'rgba(59, 130, 246, 0.3)',
          shadowBlur: isMobile ? 6 : 10,
          shadowOffsetX: isMobile ? 2 : 3,
          shadowOffsetY: isMobile ? 2 : 3
        },
        emphasis: {
          itemStyle: {
            color: {
              type: 'linear',
              x: 0,
              y: 0,
              x2: 1,
              y2: 0,
              colorStops: [
                { offset: 0, color: '#60a5fa' },
                { offset: 1, color: '#3b82f6' }
              ]
            },
            shadowBlur: isMobile ? 12 : 20,
            shadowColor: 'rgba(59, 130, 246, 0.5)'
          }
        },
        label: {
          show: !isMobile,
          position: 'right',
          formatter: (params: any) => `S/ ${params.value.toFixed(2)}`,
          color: '#1e40af',
          fontSize: isTablet ? 10 : 12,
          fontWeight: 600,
          distance: 8
        }
      },
      {
        name: 'Cantidad Pedidos',
        type: 'bar',
        data: cantidades,
        barWidth: isMobile ? '35%' : '40%',
        itemStyle: {
          color: {
            type: 'linear',
            x: 0,
            y: 0,
            x2: 1,
            y2: 0,
            colorStops: [
              { offset: 0, color: '#10b981' },
              { offset: 1, color: '#059669' }
            ]
          },
          borderRadius: [0, isMobile ? 6 : 8, isMobile ? 6 : 8, 0],
          shadowColor: 'rgba(16, 185, 129, 0.3)',
          shadowBlur: isMobile ? 6 : 10,
          shadowOffsetX: isMobile ? 2 : 3,
          shadowOffsetY: isMobile ? 2 : 3
        },
        emphasis: {
          itemStyle: {
            color: {
              type: 'linear',
              x: 0,
              y: 0,
              x2: 1,
              y2: 0,
              colorStops: [
                { offset: 0, color: '#34d399' },
                { offset: 1, color: '#10b981' }
              ]
            },
            shadowBlur: isMobile ? 12 : 20,
            shadowColor: 'rgba(16, 185, 129, 0.5)'
          }
        },
        label: {
          show: !isMobile,
          position: 'right',
          formatter: '{c}',
          color: '#065f46',
          fontSize: isTablet ? 10 : 12,
          fontWeight: 600,
          distance: 8
        }
      }
    ]
  };
});

const totalCompras = computed(() => {
  return props.data?.data?.reduce((sum, c) => sum + parseFloat(c.totalCompras), 0).toFixed(2) || '0.00';
});

const totalPedidos = computed(() => {
  return props.data?.data?.reduce((sum, c) => sum + c.cantidadPedidos, 0) || 0;
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _cache[0] || (_cache[0] = _createElementVNode("div", null, [
        _createElementVNode("h3", { class: "text-lg sm:text-xl lg:text-2xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent mb-1 sm:mb-2" }, " Top Clientes "),
        _createElementVNode("p", { class: "text-xs sm:text-sm text-slate-600 font-medium" }, "Ranking por volumen de compras")
      ], -1)),
      _createElementVNode("div", _hoisted_3, [
        _createElementVNode("div", _hoisted_4, _toDisplayString(__props.data?.data?.length || 0) + " Clientes ", 1)
      ])
    ]),
    _createElementVNode("div", _hoisted_5, [
      _createVNode(_unref(VChart), {
        option: chartOption.value,
        loading: __props.loading,
        style: _normalizeStyle({ height: chartHeight.value }),
        class: "rounded-lg"
      }, null, 8, ["option", "loading", "style"])
    ]),
    _createElementVNode("div", _hoisted_6, [
      _createElementVNode("div", _hoisted_7, [
        _createElementVNode("div", _hoisted_8, [
          _cache[2] || (_cache[2] = _createElementVNode("div", { class: "w-9 h-9 sm:w-10 sm:h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center shadow-md flex-shrink-0" }, [
            _createElementVNode("svg", {
              class: "w-4 h-4 sm:w-5 sm:h-5 text-white",
              fill: "none",
              stroke: "currentColor",
              viewBox: "0 0 24 24"
            }, [
              _createElementVNode("path", {
                "stroke-linecap": "round",
                "stroke-linejoin": "round",
                "stroke-width": "2",
                d: "M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1"
              })
            ])
          ], -1)),
          _createElementVNode("div", _hoisted_9, [
            _cache[1] || (_cache[1] = _createElementVNode("p", { class: "text-xs text-blue-600 font-semibold" }, "Total Compras", -1)),
            _createElementVNode("p", _hoisted_10, "S/ " + _toDisplayString(totalCompras.value), 1)
          ])
        ])
      ]),
      _createElementVNode("div", _hoisted_11, [
        _createElementVNode("div", _hoisted_12, [
          _cache[4] || (_cache[4] = _createElementVNode("div", { class: "w-9 h-9 sm:w-10 sm:h-10 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-lg flex items-center justify-center shadow-md flex-shrink-0" }, [
            _createElementVNode("svg", {
              class: "w-4 h-4 sm:w-5 sm:h-5 text-white",
              fill: "none",
              stroke: "currentColor",
              viewBox: "0 0 24 24"
            }, [
              _createElementVNode("path", {
                "stroke-linecap": "round",
                "stroke-linejoin": "round",
                "stroke-width": "2",
                d: "M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"
              })
            ])
          ], -1)),
          _createElementVNode("div", _hoisted_13, [
            _cache[3] || (_cache[3] = _createElementVNode("p", { class: "text-xs text-emerald-600 font-semibold" }, "Total Pedidos", -1)),
            _createElementVNode("p", _hoisted_14, _toDisplayString(totalPedidos.value), 1)
          ])
        ])
      ])
    ])
  ]))
}
}

})